import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useDialog } from "components/Actions/ActionBar/hooks";
import UploadFile, {
    useFileTypes
} from "components/Actions/dialogs/Files/UploadFile";
import { FILE_ACTIONS } from "components/Actions/types";
import Checkbox from "components/DesignComponents/Checkbox";
import QueryDropdown from "components/QueryDropdown";
import { usePermissions } from "shared/hooks/usePermissions";
import {
    COLUMN_RESOURCES,
    DROPDOWN_SIZES,
    ENDPOINTS,
    FILE_TYPES,
    ID_TYPE,
    TASK_TYPES
} from "shared/types/enums";
import { Query } from "shared/types/interfaces";

import { isUnsupportedFirmware } from "../../functions";
import { PAYLOAD_ID } from "../../types";
import { GeneralDropdownProps } from "./types";

const GeneralDropdown = ({
    secondSelectName,
    isLoading,
    additionalData,
    taskType,
    additionalBanner,
    changePayload,
    changeAdditionalData
}: GeneralDropdownProps) => {
    const { t } = useTranslation();
    const { types, isAllowedToUploadFile } = useFileTypes();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();

    const {
        isReadFileNotAllowed,
        isCreateFileNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const selectedFileType = types.find(
        type => type.value === secondSelectName
    );

    const getTooltipTitle = () => {
        if (isReadFileNotAllowed) {
            return getNoPermissionTooltipTitle(PERMISSIONS.FileRead);
        }

        const tooltipTitleMain = t("Dialog##no dropdown files##main", {
            fileName: secondSelectName
        });

        if (isCreateFileNotAllowed) {
            const secondSentence = t(
                "Dialog##no dropdown files##description no permission create file",
                { fileName: secondSelectName }
            );

            return `${tooltipTitleMain} ${secondSentence}`;
        }

        const tooltipTitleDescription = t(
            "Dialog##no dropdown files##description",
            {
                fileName: secondSelectName
            }
        );

        const secondSentence = selectedFileType?.isAllowedToRender
            ? tooltipTitleDescription
            : "";

        return `${tooltipTitleMain} ${secondSentence}`;
    };

    const [isFilteredFilesOnly, setIsFilteredFilesOnly] = useState(false);
    const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false);
    const [hasUploadedFile, setHasUploadedFile] = useState(false);

    const { value } = additionalData;

    const toggleFilteredFilesOnly = () => setIsFilteredFilesOnly(prev => !prev);

    const handleChange = (newValue: Query | null) => {
        const isUploadFirmwareTask = taskType === TASK_TYPES.TxFirmware;

        const isUnsupportedFile =
            isUploadFirmwareTask &&
            isUnsupportedFirmware(newValue?.attributes || null);

        const payload =
            newValue !== null
                ? {
                      id: PAYLOAD_ID.FileId,
                      value: newValue.id,
                      isUnsupportedFile
                  }
                : null;

        changePayload(payload);

        changeAdditionalData({
            value: newValue,
            ...(isUploadFirmwareTask
                ? { fileAttributes: newValue?.attributes || null }
                : {})
        });
    };

    const tooltipTitle = isUploadDialogVisible ? "" : getTooltipTitle();

    const isAllowedToUploadFileValue = isAllowedToUploadFile(
        secondSelectName as FILE_TYPES
    );

    return (
        <>
            <QueryDropdown
                data-testid="second-select-dropdown"
                fullWidth
                isRequired
                noOptionsTooltip={{
                    title: tooltipTitle,
                    fileType: secondSelectName as FILE_TYPES,
                    hasUploadedFile,
                    ...(isAllowedToUploadFileValue
                        ? {
                              buttonText: t("Button##upload file"),
                              onButtonClick: () =>
                                  openDialog(FILE_ACTIONS.UploadFiles)
                          }
                        : {})
                }}
                value={value}
                idType={ID_TYPE.Id}
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t(`General##${secondSelectName}`)}
                readOnly={isLoading}
                description={{
                    resource: [COLUMN_RESOURCES.Company, COLUMN_RESOURCES.Name]
                }}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                resource={
                    isReadFileNotAllowed
                        ? ""
                        : `${ENDPOINTS.Files}?type=${secondSelectName}`
                }
                isRootRequired={isFilteredFilesOnly}
                isCompanyIdRequired={isFilteredFilesOnly}
                change={(_, fileValue) => handleChange(fileValue)}
            />

            <Checkbox
                label={t("Dialog##show files for selected companies only")}
                onChange={toggleFilteredFilesOnly}
                checked={isFilteredFilesOnly}
                disabled={isLoading}
                customStyle={{
                    marginTop: "16px"
                }}
            />

            <UploadFile
                isOpen={isOpenDialog[FILE_ACTIONS.UploadFiles]}
                selectedType={selectedFileType}
                close={closeDialog}
                onEnter={() => setIsUploadDialogVisible(true)}
                onExited={() => setIsUploadDialogVisible(false)}
                successCallback={() => setHasUploadedFile(prev => !prev)}
            />

            {additionalBanner}
        </>
    );
};

export default GeneralDropdown;
